import { ChangeEvent, FC, type JSX, useEffect, useState } from 'react';

import { capitalizeFirstLetter } from '@eneco/common/string';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import useDC from '@eneco/dc/useDC';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Heading,
  InputCombobox,
  InputEmail,
  InputTelephone,
  InputText,
  NotificationBox,
  RadioGroup,
  RadioTile,
  Stack,
  Text,
  TextLink,
} from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { CompanyDetailsStepRendering } from '@sitecore/types/manual/smeFlowExperiment/SMECompanyDetailsStep';
import { useMachine, useSelector } from '@xstate/react';
import { Controller, useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';
import { formatInitials } from '../../helpers';
import { chamberOfCommerceSearchMachine } from '../../machines';

interface FormFields {
  companyName: string;
  chamberOfCommerceNumber: string;
  initials: string;
  surnamePreposition: string;
  surname: string;
  isLegallyResponsible: string;
  phoneNumber: string;
  emailAddress?: string;
  C1?: boolean;
  C2?: boolean;
  C3?: boolean;
  sufficientAge?: boolean;
}

export const CompanyDetailsStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const companyName = useFlowSelector(state => state.context.companyName);
  const chamberOfCommerceNumber = useFlowSelector(state => state.context.chamberOfCommerceNumber);
  const initials = useFlowSelector(state => state.context.initials);
  const surnamePreposition = useFlowSelector(state => state.context.surnamePreposition);
  const surname = useFlowSelector(state => state.context.surname);
  const phoneNumber = useFlowSelector(state => state.context.phoneNumber);
  const emailAddress = useFlowSelector(state => state.context.emailAddress);
  const isPersonalDetailsEditProhibited = useFlowSelector(state => state.context.isPersonalDetailsEditProhibited);
  const C1 = useFlowSelector(state => state.context.C1);
  const C2 = useFlowSelector(state => state.context.C2);
  const C3 = useFlowSelector(state => state.context.C3);

  const [, sendTochamberOfCommerceSearchMachine, chamberOfCommerceSearchActor] =
    useMachine(chamberOfCommerceSearchMachine);
  const results = useSelector(chamberOfCommerceSearchActor, state => state.context.results);
  const isFetching = useSelector(chamberOfCommerceSearchActor, state => state.value === 'FETCHING');
  const companyNameOptions = results?.map((result: { name?: string }) => result?.name ?? '') || [];

  const { SmeCompanyDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SmeCompanyDetailsStep: CompanyDetailsStepRendering;
  }>();

  const formSchema = yup.object({
    companyName: yup.string().required(textualData?.companyDetails?.companyNameFormField?.value?.requiredMessage),
    chamberOfCommerceNumber: yup
      .string()
      .required(textualData?.companyDetails?.chamberOfCommerceNumberFormField?.value?.requiredMessage),
    initials: yup
      .string()
      .required(textualData?.personalDetails?.initialsFormField?.value?.requiredMessage)
      .test(
        'initials max',
        textualData?.personalDetails?.initialsFormField?.value?.validationMaxValueMessage ?? '',
        (value = '') => {
          return value.toUpperCase().replace(/[^A-Z]/g, '').length <= 6;
        },
      ),
    surnamePreposition: yup.string(),
    surname: yup.string().required(textualData?.personalDetails?.surnameFormField?.value.requiredMessage),
    isLegallyResponsible: yup
      .string()
      .oneOf(['true'], textualData?.personalDetails.isLegallyResponsibleRadio?.value?.requiredMessage),
    phoneNumber: yup
      .string()
      .required(textualData?.contactDetails?.phoneNumberFormField?.value?.requiredMessage)
      .matches(
        /^(?:0[0-57-9]\d{2}[-\s]?\d{6}|0[0-57-9]\d[-\s]?\d{7}|06[-\s]?\d{8})$/,
        textualData?.contactDetails?.phoneNumberFormField?.value?.validationMessage,
      ),
    emailAddress: emailValidation({
      emailMessage: textualData?.contactDetails?.emailAddressFormField?.value?.validationMessage,
      requiredMessage: textualData?.contactDetails?.emailAddressFormField?.value?.requiredMessage,
    }),
    C1: yup.boolean(),
    C2: yup.boolean(),
    C3: yup.boolean(),
    sufficientAge: yup
      .boolean()
      .oneOf([true], textualData?.personalDetails?.ofLegalAgeFormField?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    register,
    trigger,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onTouched',
    defaultValues: {
      companyName,
      chamberOfCommerceNumber,
      initials,
      surnamePreposition,
      surname,
      isLegallyResponsible: 'true',
      sufficientAge: true,
      phoneNumber,
      emailAddress,
      C1,
      C2,
      C3,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const watchCompanyName = watch('companyName');
  const watchIsLegallyResponsible = watch('isLegallyResponsible');

  const selectedChamberOfCommerceNumber =
    results?.find((result: { name?: string; chamberOfCommerceNumber?: string }) => result.name === watchCompanyName)
      ?.chamberOfCommerceNumber ?? '';

  useDebounce(
    () => {
      const isCompanyNameInOptions = companyNameOptions.includes(watchCompanyName);
      if (watchCompanyName && !isCompanyNameInOptions) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = {
          type: 'GET_COMPANY_OPTIONS',
          config: { businessUnit, label },
          values: {
            companyName: watchCompanyName,
          },
        };
        sendTochamberOfCommerceSearchMachine(payload);
      }
    },
    500,
    [watchCompanyName],
  );

  useEffect(() => {
    if (selectedChamberOfCommerceNumber) {
      setValue('chamberOfCommerceNumber', selectedChamberOfCommerceNumber);
      trigger('companyName');
    }
  }, [selectedChamberOfCommerceNumber, setValue, trigger]);

  useEffect(() => {
    trigger('isLegallyResponsible');
  }, [watchIsLegallyResponsible, trigger]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          companyName: formData.companyName,
          chamberOfCommerceNumber: formData.chamberOfCommerceNumber,
          initials: formData.initials,
          surnamePreposition: formData.surnamePreposition || undefined,
          surname: formData.surname,
          phoneNumber: formData.phoneNumber,
          emailAddress: formData.emailAddress,
          C1: formData.C1,
          C2: formData.C2,
          C3: formData.C3,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const kvkNumberOutput = selectedChamberOfCommerceNumber && (
    <Heading as="h3" size="2XS">
      {textualData?.companyDetails?.chamberOfCommerceNumberFormField?.value?.label}: {selectedChamberOfCommerceNumber}
    </Heading>
  );

  const generateInputElement = (value: string, label: string): JSX.Element => {
    return (
      <Stack.Item grow>
        <Stack>
          <RadioTile value={value} key={value}>
            {label}
          </RadioTile>
        </Stack>
      </Stack.Item>
    );
  };

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <Box paddingBottom={4}>
        <Stack direction="column" gap={6}>
          <InputCombobox
            {...register('companyName')}
            defaultValue={companyName}
            onChange={value => {
              const newValue = value.trim();
              if (newValue !== watchCompanyName) {
                setValue('companyName', newValue);
              }
            }}
            label={textualData?.companyDetails?.companyNameFormField?.value?.label ?? ''}
            placeholder={textualData?.companyDetails?.companyNameFormField?.value?.placeholder}
            hint={textualData?.companyDetails?.companyNameFormField?.value?.hint}
            error={errors.companyName?.message}
            options={isFetching ? [textualData?.content?.companySearchLoadingText?.value ?? ''] : companyNameOptions}
          />
          {kvkNumberOutput}
        </Stack>
      </Box>

      <Heading as="h3" size="XS">
        {textualData?.personalDetails.isLegallyResponsibleRadio?.value?.label}
      </Heading>
      <Controller
        control={control}
        name="isLegallyResponsible"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            label=""
            name={name}
            direction="row"
            value={value}
            onValueChange={onChange}
            error={errors.isLegallyResponsible?.message}>
            {textualData?.personalDetails.isLegallyResponsibleRadio?.value?.options.map(option =>
              generateInputElement(option.name, option.label),
            )}
          </RadioGroup>
        )}
      />
      <Grid columns="2" columnGap="3">
        <InputText
          {...register('initials', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              setValue('initials', formatInitials(event.target.value));
            },
          })}
          label={textualData?.personalDetails?.initialsFormField?.value?.label ?? ''}
          placeholder={textualData?.personalDetails?.initialsFormField?.value?.placeholder}
          hint={textualData?.personalDetails?.initialsFormField?.value?.hint}
          error={errors.initials?.message}
        />
        <InputText
          {...register('surnamePreposition')}
          label={textualData?.personalDetails?.surnamePrepositionFormField?.value?.label ?? ''}
          placeholder={textualData?.personalDetails?.surnamePrepositionFormField?.value?.placeholder}
          hint={textualData?.personalDetails?.surnamePrepositionFormField?.value?.hint}
          error={errors.surnamePreposition?.message}
        />
      </Grid>
      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
        placeholder={textualData?.personalDetails?.surnameFormField?.value?.placeholder}
        hint={textualData?.personalDetails?.surnameFormField?.value?.hint}
        error={errors.surname?.message}
      />
      <Box paddingBottom={3}>
        <Checkbox
          label={textualData?.personalDetails?.ofLegalAgeFormField?.value?.label ?? ''}
          {...register('sufficientAge')}
          weight="bold"
          error={errors.sufficientAge?.message}
        />
      </Box>

      <Text size="BodyS" color="textPrimary">
        {textualData?.contactDetails?.text?.value}
      </Text>
      <InputTelephone
        {...register('phoneNumber')}
        label={textualData?.contactDetails?.phoneNumberFormField?.value?.label ?? ''}
        placeholder={textualData?.contactDetails?.phoneNumberFormField?.value?.placeholder}
        hint={textualData?.contactDetails?.phoneNumberFormField?.value?.hint}
        autoComplete="tel"
        error={errors.phoneNumber?.message}
      />
      <Stack.Item>
        <InputEmail
          {...register('emailAddress')}
          label={textualData?.contactDetails?.emailAddressFormField?.value?.label ?? ''}
          placeholder={textualData?.contactDetails?.emailAddressFormField?.value?.placeholder}
          hint={textualData?.contactDetails?.emailAddressFormField?.value?.hint}
          autoComplete="email"
          error={errors.emailAddress?.message}
        />

        {textualData?.contactDetails?.emailAddressText?.value && (
          <Box paddingTop="4">
            <Text size="BodyS" color="textLowEmphasis">
              <RichText html={textualData?.contactDetails?.emailAddressText?.value} />
            </Text>
          </Box>
        )}
      </Stack.Item>

      <Stack.Item>
        <NotificationBox
          isAlert={false}
          variant={textualData?.residentialFunction?.residenceFunctionNotification?.value.variant}
          text={
            <Text size="BodyS">
              <RichText html={textualData?.residentialFunction?.residenceFunctionNotification?.value.content} />{' '}
              <TrackedDialog
                isOpen={isModalOpen}
                setOpen={setIsModalOpen}
                title={textualData?.residentialFunction?.residenceFunctionDialog?.value?.title}
                trigger={
                  <TextLink onClick={() => setIsModalOpen(true)}>
                    {textualData?.residentialFunction?.residenceFunctionDialog?.value?.triggerText}
                  </TextLink>
                }>
                <Stack gap={8}>
                  <Stack.Item>
                    <RichText html={textualData?.residentialFunction?.residenceFunctionDialog?.value?.content} />
                  </Stack.Item>
                  <Stack.Item>
                    <Button type={'button'} onClick={() => setIsModalOpen(false)}>
                      {textualData?.residentialFunction?.residenceFunctionDialog?.value?.submitButtonText}
                    </Button>
                  </Stack.Item>
                </Stack>
              </TrackedDialog>
            </Text>
          }
        />
      </Stack.Item>
      {!isPersonalDetailsEditProhibited && (
        <>
          <Stack.Item>
            <Stack direction="column" gap="4">
              <Heading as="h3" size="3XS">
                {textualData?.optIn?.title?.value}
              </Heading>
              <RichText html={textualData?.optIn?.content?.value} />
            </Stack>
          </Stack.Item>

          <Stack.Item>
            <Stack direction="column" gap="2">
              <Checkbox
                label={textualData?.optIn?.productOffersFirstPartyCheckBox?.value?.label ?? ''}
                {...register('C1')}
              />
              <Checkbox
                label={textualData?.optIn?.productOffersPhoneCommunicationConsentFormField?.value?.label ?? ''}
                {...register('C2')}
              />
              <Checkbox
                label={textualData?.optIn?.productOffersThirdPartyCheckBox?.value?.label ?? ''}
                {...register('C3')}
              />
            </Stack>
          </Stack.Item>
        </>
      )}
      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
